/** @jsx jsx */
/** @jsxFrag */
import { ClassNames, jsx } from '@emotion/react';
import ButtonLink from 'components/ButtonLink';
import Container from 'components/Container';
import EuropeMap from 'icons/EuropeMap';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Contact } from 'types/ContactData';
import { Country } from 'types/Map';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ContactPageInfoProps extends React.HTMLAttributes<HTMLDivElement> {
  contact: Contact;
  mapValues: Country[];
}

const ContactPageInfo = React.forwardRef<HTMLDivElement, ContactPageInfoProps>(({ mapValues, contact }, ref) => {
  const { company, sales, techSupport } = contact;

  return (
    <ClassNames>
      {({ theme }) => (
        <>
          <Container maxWidth='lg' css={{ flexDirection: 'column' }}>
            <div
              ref={ref}
              css={{
                '@media (min-width: 960px)': { padding: theme.spacing(4, 6) },
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'flex-start',
                padding: theme.spacing(2, 0),
                position: 'relative',
              }}
            >
              <EuropeMap
                mapValues={mapValues}
                css={{
                  '@media (min-width: 425px)': { right: -80, width: '120%' },
                  '@media (min-width: 600px)': { right: -150 },
                  '@media (min-width: 960px)': { height: '120%', right: -280, top: -250, width: '120%' },
                  height: 350,
                  position: 'absolute',
                  right: -35,
                  top: -80,
                  transform: 'scale(2)',
                  width: '100%',
                  zIndex: -1,
                }}
              />
              <div
                css={{
                  '@media (min-width: 600px)': { maxWidth: 290 },
                  '@media (min-width: 960px)': { marginBottom: theme.spacing(4), maxWidth: 329 },
                  ...theme.typography.h2,
                  marginBottom: theme.spacing(2),
                  maxWidth: 230,
                  textAlign: 'left',
                }}
              >
                <FormattedMessage id={'contacts.title'} />
              </div>
              {sales && (
                <ContactInfoContact>
                  <p>
                    <FormattedMessage id={'contacts.sales'} />
                  </p>
                  <a href={`tel:${sales?.phone}`}>{sales?.phone}</a>
                  <a href={`mailto:${sales?.email}`}>{sales?.email}</a>
                </ContactInfoContact>
              )}
              {techSupport && (
                <ContactInfoContact>
                  <p>
                    <FormattedMessage id={'contacts.techsupport'} />
                  </p>
                  <a href={`tel:${techSupport.phone}`}>{techSupport.phone}</a>
                  <a href={`mailto:${techSupport.email}`}>{techSupport.email}</a>
                </ContactInfoContact>
              )}
              <ButtonLink isExternal to='https://status.appon.cloud/' css={{ marginTop: theme.spacing(2) }}>
                <FormattedMessage id={'services.status'} />
              </ButtonLink>
              {company && (
                <ContactInfoContact
                  css={{
                    '@media (min-width: 600px)': { marginTop: theme.spacing(6), maxWidth: 290 },
                    '@media (min-width: 960px)': { maxWidth: 350 },
                    fontSize: 11,
                    maxWidth: 200,
                  }}
                >
                  <span>{company.name}</span>
                  <span>{`${company.street} ${company.city}`}</span>
                  <span>{company.country}</span>
                  <br />
                  <span>{company.ico}</span>
                  <span>{company.dic}</span>
                  <span>{company.registrationInfo}</span>
                </ContactInfoContact>
              )}
            </div>
          </Container>
        </>
      )}
    </ClassNames>
  );
});

export default ContactPageInfo;
ContactPageInfo.displayName = 'ContactPageInfo';

const ContactInfoContact: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  return (
    <ClassNames>
      {({ theme, css, cx }) => (
        <div
          css={cx(
            css({
              ...theme.typography.body1,
              '& > a': { color: theme.palette.text.primary, textDecoration: 'none' },
              '& > a:hover': { textDecoration: 'underline' },
              '& > p': { color: theme.palette.primary.main, fontWeight: theme.typography.fontWeightMedium },
              '@media(min-width: 600px)': { fontSize: 14 },
              '@media(min-width: 960px)': { fontSize: 16 },
              display: 'flex',
              flexDirection: 'column',
              fontSize: 12,
              padding: theme.spacing(1, 0),
            }),
            className,
          )}
        >
          {children}
        </div>
      )}
    </ClassNames>
  );
};

ContactInfoContact.displayName = 'ContactInfoContact';
